type RequestDetailsType = {
  method: string;
  path: string;
  status: number;
  responseText?: string;
  stack?: string;
};

type ClientResultType = { error?: unknown; response: Response };

type ClientMethodType = (
  path: string,
  init?: unknown,
) => Promise<ClientResultType>;

const requestDetailsKey = Symbol('requestDetails');

const wrappedMethods = ['GET', 'PUT', 'POST', 'DELETE', 'PATCH'] as const;

const maxTextErrors = 20;
const textErrorDetails = new Map<string, RequestDetailsType>();

export function getRequestDetails(error: unknown) {
  if (typeof error === 'string') return textErrorDetails.get(error);
  if (!error || typeof error !== 'object') return undefined;
  return (error as Record<symbol, RequestDetailsType | undefined>)[
    requestDetailsKey
  ];
}

type StackHolderType = { name: string; message: string; stack?: string };

function stackFromCaller(
  caller: (...args: never[]) => unknown,
  name: string,
  message: string,
) {
  const holder: StackHolderType = { name, message };
  if (
    'captureStackTrace' in Error &&
    typeof Error.captureStackTrace === 'function'
  ) {
    Error.captureStackTrace(holder, caller);
  }
  return holder.stack || new Error(message).stack;
}

function tagError(error: unknown, details: RequestDetailsType) {
  if (typeof error === 'string') {
    textErrorDetails.delete(error);
    textErrorDetails.set(error, details);
    if (textErrorDetails.size > maxTextErrors) {
      textErrorDetails.delete(textErrorDetails.keys().next().value ?? '');
    }
    return;
  }
  if (!error || typeof error !== 'object') return;
  Object.defineProperty(error, requestDetailsKey, {
    value: details,
    configurable: true,
  });
}

export function withRequestDetails<
  ClientType extends Record<(typeof wrappedMethods)[number], unknown>,
>(client: ClientType): ClientType {
  const wrappedClient = { ...client };
  for (const method of wrappedMethods) {
    const send = client[method] as ClientMethodType;
    const sendAndTag: ClientMethodType = async (path, init) => {
      const result = await send(path, init);
      const { error, response } = result;
      if (error && !response.ok) {
        const message = `${method} ${path}: ${response.status}`;
        tagError(error, {
          method,
          path,
          status: response.status,
          responseText:
            typeof error === 'string' ? error.slice(0, 300) : undefined,
          stack: stackFromCaller(sendAndTag, 'ApiError', message),
        });
      }
      return result;
    };
    Object.assign(wrappedClient, { [method]: sendAndTag });
  }
  return wrappedClient;
}
